/* Auto-advancing product rails. No dependencies.
 *
 * Drives any [data-rail-autoplay] slider by scrolling its track one card at
 * a time and wrapping back to the start.
 *
 * The timer runs from the moment the rail is found and each tick decides
 * whether to act, rather than starting and stopping the timer from an
 * IntersectionObserver. Observer edges are easy to miss for a rail that is
 * below the fold at load, which left rails sitting still.
 *
 * Scrolling assigns scrollLeft directly and lets CSS scroll-behavior do the
 * animating. scrollTo({behavior:'smooth'}) is silently ignored in some
 * environments, which also left rails sitting still. */
(() => {
  if (window.__railAutoplay) return;
  window.__railAutoplay = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Rail {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('[data-slider-track]');
      this.delay = Math.max(2000, parseInt(el.dataset.railAutoplay, 10) * 1000 || 4000);
      this.paused = false;
      this.stopped = false;

      if (!this.track || REDUCED.matches) return;

      /* Any deliberate interaction hands control back to the visitor. */
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((evt) =>
        this.el.addEventListener(evt, () => this.stop(), { passive: true, once: true })
      );
      this.el.addEventListener('mouseenter', () => (this.paused = true));
      this.el.addEventListener('mouseleave', () => (this.paused = false));
      this.el.addEventListener('focusin', () => (this.paused = true));
      this.el.addEventListener('focusout', () => (this.paused = false));

      this.timer = setInterval(() => this.step(), this.delay);
    }

    onScreen() {
      const b = this.el.getBoundingClientRect();
      return b.top < window.innerHeight && b.bottom > 0;
    }

    step() {
      if (this.stopped || this.paused || document.hidden || !this.onScreen()) return;

      const track = this.track;
      const first = track.firstElementChild;
      if (!first) return;

      /* Measured every tick: a rail is often laid out before its images
         have loaded, so whether it overflows is not settled up front. */
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 4) return;

      const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      const stride = first.getBoundingClientRect().width + gap;

      /* Within half a card of the end counts as the end, because sub-pixel
         widths mean scrollLeft rarely lands exactly on it. */
      const atEnd = track.scrollLeft >= maxScroll - stride * 0.5;
      track.scrollLeft = atEnd ? 0 : Math.min(track.scrollLeft + stride, maxScroll);
    }

    stop() {
      this.stopped = true;
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
  }

  const init = () =>
    document.querySelectorAll('[data-rail-autoplay]').forEach((el) => {
      if (!el.__rail) el.__rail = new Rail(el);
    });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', init);
})();
